import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/navbar/index.jsx';
import Footer from '../../components/Footer/Footer.js';
import WaFloat from '../../components/WaFloat/WaFloat.jsx';
import { useReveal } from '../../hooks/useReveal.js';

const steps = [
  { n: 1, h: 'Financial health audit', p: 'Comprehensive review of P&L, balance sheet, cash flow statements, and existing debt structure. We identify every element that will raise red flags in investor due diligence — related-party transactions, inflated expenses, revenue recognition issues, and working capital anomalies.', out: 'Full diagnostic report with priority action list' },
  { n: 2, h: 'Financial restructuring & cleanup', p: 'Operational work alongside your CA and finance team. Separation of personal and business expenses, EBITDA normalisation, debt restructuring where necessary, and MIS system improvement. We bring the company to institutional due diligence standards.', out: 'Investor-ready financials in 60–90 days' },
  { n: 3, h: 'Capital structure advisory', p: 'Recommend the right capital mix — equity, debt, or hybrid. Model dilution scenarios. Advise on valuation positioning and realistic deal terms. Help promoters understand what they\'re agreeing to before they walk into investor meetings.', out: 'Capital structure plan with scenario modelling' },
  { n: 4, h: 'Investor narrative & IM', p: 'Build the Information Memorandum, financial projections, management presentation, and deal teaser. Write the growth story in investor language — TAM, CAGR, unit economics, competitive positioning, and use of funds. This is what investors actually read before agreeing to meet.', out: 'Full IM + pitch deck + financial model' },
  { n: 5, h: 'Investor connect and transaction support', p: 'Works directly with AQT Direct\'s investor network to facilitate structured introductions. Manages investor queries, information sharing, engagement support, and transaction documentation guidance. The AFO is the financial face of the business throughout.', out: 'Capital connected, engagement closed' },
  { n: 6, h: 'Post-funding governance', p: 'Stays engaged after capital closes. Sets up monthly MIS reporting packs, investor dashboard, board-level financial reporting, and utilisation tracking. Builds long-term investor trust — and creates repeat engagement opportunities.', out: 'Professional investor relations from day one' },
];

const problems = [
  { h: 'No full-time CFO', p: 'Promoter handles finances, CA handles compliance — no one is focused on making the company attractive to investors.' },
  { h: 'Weak financial presentation', p: 'Mixed personal and business expenses, unclear EBITDA, poor working capital management — all red flags for investors.' },
  { h: 'No investor narrative', p: 'Companies can\'t articulate their growth story, TAM, or competitive moat in a language investors respond to.' },
  { h: 'Wrong capital structure', p: 'Taking debt when equity is better, or diluting too early — costly mistakes an experienced AFO prevents.' },
];

const idealClients = [
  { h: '₹20–500 Cr revenue', p: 'Mid-market company with proven revenue but no institutional-grade financial infrastructure.' },
  { h: 'No full-time CFO', p: 'Promoter-managed finances. CA handles compliance only. No one focused on investor readiness.' },
  { h: 'Raising in 6–18 months', p: 'Has a funding need on the horizon — capex, expansion, acquisition — and needs to prepare now.' },
  { h: 'Failed a raise before', p: 'Went to investors, didn\'t close. Most likely a financials or narrative problem — exactly what AFO fixes.' },
];


export default function AFO() {
  useReveal();
  return (
    <>
      <Navbar />

      {/* HERO */}
      <div className="afo-page-hero">
        <div className="afo-bg-circle" />
        <div className="afo-page-inner">
          <span className="sec-tag reveal">AQT Financial Officer</span>
          <h2 className="sec-h reveal">Your Business,<br /><em>Investment-Ready.</em></h2>
          <p className="sec-sub reveal reveal-delay-1">
            The AQT Financial Officer (AFO) is an embedded, senior advisory professional who works inside your business — building financial discipline, structuring your growth story, and connecting you with the right capital partners.
          </p>
          <div className="afo-hero-btns reveal reveal-delay-2">
            <Link className="btn-primary" to="/contactus">Request an AFO →</Link>
            <Link className="btn-white-outline" to="/contactus">Contact Us</Link>
          </div>
        </div>
      </div>

      <hr className="rule" />

      {/* THE PROBLEM */}
      <section className="section">
        <span className="sec-tag reveal">The Problem</span>
        <h2 className="sec-h reveal">Why Businesses Struggle<br />to <em>Access Capital</em></h2>
        <p className="sec-sub reveal reveal-delay-1">Most growing businesses want capital but aren't structured for it. The AFO engagement bridges that gap — from inside your business.</p>
        <div className="ideal-grid reveal reveal-delay-2">
          {problems.map(({ h, p }) => (
            <div className="ideal-card" key={h}><h4>{h}</h4><p>{p}</p></div>
          ))}
        </div>
      </section>

      <hr className="rule" />

      {/* STEPS */}
      <section className="section section-off">
        <span className="sec-tag reveal">What We Do</span>
        <h2 className="sec-h reveal">The AFO Engagement —<br /><em>Step by Step</em></h2>
        <p className="sec-sub reveal reveal-delay-1">A structured programme that takes a business from growth ambition to capital-ready — and then connects it with aligned investors.</p>
        <div className="afo-steps reveal reveal-delay-2">
          {steps.map(({ n, h, p, out }) => (
            <div className="afo-step-row" key={n}>
              <div className="afo-step-num">{n}</div>
              <div>
                <h3>{h}</h3>
                <p>{p}</p>
                <span className="outcome">Outcome: {out}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* IDEAL CLIENTS */}
      <section className="section">
        <span className="sec-tag reveal">Ideal Clients</span>
        <h2 className="sec-h reveal">Is This<br /><em>Right for You?</em></h2>
        <p className="sec-sub reveal reveal-delay-1">The AFO model is designed for a specific type of company at a specific stage of growth.</p>
        <div className="ideal-grid reveal reveal-delay-2">
          {idealClients.map(({ h, p }) => (
            <div className="ideal-card" key={h}><h4>{h}</h4><p>{p}</p></div>
          ))}
        </div>
      </section>

      <div className="cta-strip reveal">
        <h2>Ready to Make Your Business<br /><em>Capital-Ready?</em></h2>
        <p>Talk to our advisory team about which AFO plan suits your business stage and growth objectives.</p>
        <div className="cta-btns">
          <Link className="btn-white" to="/contactus">Talk to Our Team</Link>
        </div>
      </div>

      <Footer />
      <WaFloat />
    </>
  );
}
